import { Checkbox } from '@/shared/ui/checkbox'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { shouldShowValidateField } from '@/shared/lib/data-field-config'
import type { DataFieldTypeAndFlagsProps } from './types'

export const DataFieldTypeAndFlags = ({
  instrumentTaskDataFieldTypeId,
  typeOptions,
  fieldTypeCode,
  required,
  requiresValidation,
  isConfidential,
  active = true,
  showActive = false,
  onTypeChange,
  onRequiredChange,
  onRequiresValidationChange,
  onIsConfidentialChange,
  onActiveChange,
  labels,
  idPrefix = 'data-field',
  checkboxLayout = 'inline',
  showTypeSelect = true,
  showFlags = true,
  showRequiresValidation = true,
  showFieldCheckboxes = true,
  showValidateCheckbox = true,
  validateFieldDisabled = false,
  typeRequired = false,
  typeInvalid = false,
  lotLinked = false,
}: DataFieldTypeAndFlagsProps) => {
  const showValidateField = shouldShowValidateField(fieldTypeCode) && showRequiresValidation

  const requiredControl =
    checkboxLayout === 'inline' ? (
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={required}
          onCheckedChange={(checked) => onRequiredChange(checked === true)}
        />
        {labels.required}
      </label>
    ) : (
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-required`}
          checked={required}
          onCheckedChange={(checked) => onRequiredChange(checked === true)}
        />
        <Label htmlFor={`${idPrefix}-required`} className="font-normal">
          {labels.required}
        </Label>
      </div>
    )

  const confidentialControl =
    checkboxLayout === 'inline' ? (
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={isConfidential}
          onCheckedChange={(checked) => onIsConfidentialChange(checked === true)}
        />
        {labels.isConfidential}
      </label>
    ) : (
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-confidential`}
          checked={isConfidential}
          onCheckedChange={(checked) => onIsConfidentialChange(checked === true)}
        />
        <Label htmlFor={`${idPrefix}-confidential`} className="font-normal">
          {labels.isConfidential}
        </Label>
      </div>
    )

  const validateControl =
    showValidateField &&
    (checkboxLayout === 'inline' ? (
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={requiresValidation}
          disabled={validateFieldDisabled}
          onCheckedChange={(checked) => {
            if (validateFieldDisabled) return
            onRequiresValidationChange(checked === true)
          }}
        />
        {labels.validateFieldTitle}
        {lotLinked && labels.acceptableValuesFromLot && (
          <span className="text-muted-foreground text-xs">{labels.acceptableValuesFromLot}</span>
        )}
      </label>
    ) : (
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-requires-validation`}
          checked={requiresValidation}
          disabled={validateFieldDisabled}
          onCheckedChange={(checked) => {
            if (validateFieldDisabled) return
            onRequiresValidationChange(checked === true)
          }}
        />
        <Label htmlFor={`${idPrefix}-requires-validation`} className="font-normal">
          {labels.validateFieldTitle}
        </Label>
        {lotLinked && labels.acceptableValuesFromLot && (
          <span className="text-muted-foreground text-xs">{labels.acceptableValuesFromLot}</span>
        )}
      </div>
    ))

  const activeControl =
    showActive &&
    labels.active &&
    onActiveChange &&
    (checkboxLayout === 'inline' ? (
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={active}
          onCheckedChange={(checked) => onActiveChange(checked === true)}
        />
        {labels.active}
      </label>
    ) : (
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-active`}
          checked={active}
          onCheckedChange={(checked) => onActiveChange(checked === true)}
        />
        <Label htmlFor={`${idPrefix}-active`} className="font-normal">
          {labels.active}
        </Label>
      </div>
    ))

  return (
    <>
      {showTypeSelect && (
        <div className="space-y-1.5">
          <Label>
            {labels.fieldTypeLabel}
            {typeRequired && <span className="ml-0.5 text-destructive">*</span>}
          </Label>
          <Select
            value={instrumentTaskDataFieldTypeId || '__none__'}
            onValueChange={(value) => onTypeChange(value === '__none__' ? '' : value)}
          >
            <SelectTrigger aria-invalid={typeInvalid || undefined}>
              <SelectValue placeholder={labels.fieldTypePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((typeOption) => (
                <SelectItem key={typeOption.id} value={String(typeOption.id)}>
                  {typeOption.dataFieldTypeDesc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {typeInvalid && labels.fieldTypeRequired && (
            <p className="text-sm text-destructive">{labels.fieldTypeRequired}</p>
          )}
        </div>
      )}

      {showFlags && (
        <div
          className={
            checkboxLayout === 'inline'
              ? 'space-y-2'
              : 'flex flex-col gap-3 sm:flex-row sm:items-center'
          }
        >
          {checkboxLayout === 'inline' ? (
            <>
              {showFieldCheckboxes && (
                <div className="flex flex-wrap items-center gap-4">
                  {requiredControl}
                  {confidentialControl}
                  {activeControl}
                </div>
              )}
              {showValidateCheckbox && validateControl && (
                <div className="flex items-center">{validateControl}</div>
              )}
            </>
          ) : (
            <>
              {showFieldCheckboxes && requiredControl}
              {showFieldCheckboxes && confidentialControl}
              {showValidateCheckbox && validateControl}
              {showFieldCheckboxes && activeControl}
            </>
          )}
        </div>
      )}
    </>
  )
}
